import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourneeDuJour } from '../journeeDuJour';
import { useAuPouce, useCouche } from '../../partage/surface';
import { aujourdhui, enFrancais } from '../../partage/datesEnMots';
import type { ArriveeSurLaFeuille } from '../../partage/navigationDeLaFeuille';
import { HautDuPouce } from '../../partage/HautDuPouce';
import { CHANGER_MA_JOURNEE, MA_JOURNEE, majuscule, ON_RECOMMENCE, RECOMMENCER, REGLER_MA_JOURNEE, TA_JOURNEE } from '../../partage/vocabulaire';
import { BandeauDeLaVeille, BandeauDuLendemain } from './BandeauxDeLaJournee';
import { ChangerMaJournee } from './ChangerMaJournee';
import { JourneeDuJour } from './JourneeDuJour';
import { JourneeAuPouce } from './JourneeAuPouce';
import { PlanifierUnJour } from './PlanifierUnJour';
import { PreparerUnJour } from './PreparerUnJour';
import { poseLaQuestion } from './questionDuJour';
import './Journee.css';

/**
 * L'onglet Journée du téléphone (chantier `telephone-cadre`, ticket 01, décisions d'Igor du
 * 2026-10-05 ; maquette `design/telephone.html`, la barre `.tabs`).
 *
 * La journée du jour : en tête, les bandeaux de la veille et du lendemain, puis la journée en
 * cours (`JourneeAuPouce`). **Rien de prévu** (chantier `telephone-journee-agenda`, ticket 01, décisions 7 et 8) :
 * l'onglet pose la question (combien de temps, quel courage, dès quelle heure, où), puis
 * « Propose-moi une journée » ou « Je choisis moi-même » (`PlanifierUnJour`). Elle n'est posée
 * que par l'onglet, qu'on touche : l'app s'ouvre sur Maintenant. Une journée qui a une tâche,
 * même une seule posée depuis l'Agenda, ne la pose pas. Le serveur dit le jour et les bandeaux.
 *
 * **Une journée prévue** (même chantier, ticket 04) : la journée en cours (`JourneeAuPouce`) ;
 * « Changer ma journée » l'ouvre à modifier (`ChangerMaJournee`), dont « Recommencer » repose la
 * question en plein écran, sans rien écrire avant « C'est ma journée », qui garde les tâches déjà
 * faites à leur heure (spec `maintenant`, « Rouverte aujourd'hui, une journée garde ses tâches faites »).
 *
 * Au-dessus de 720 px, rien ne change : l'invitation « Planifier ma journée » et son panneau.
 */
export function Journee() {
  const naviguer = useNavigate();
  /*
   * Couché, l'écran du téléphone (chantier telephone-couche-suite, 03) : la bande des pastilles et
   * la fiche posée ; l'écran dépasse 720 px, sans ça il retomberait sur celui de l'ordinateur.
   */
  const couche = useCouche();
  const auPouce = useAuPouce() || couche;
  const duJour = useJourneeDuJour();
  const [planifier, setPlanifier] = useState(false);
  const [ecran, setEcran] = useState<'en-cours' | 'changer' | 'recommencer'>('en-cours');
  const [annonce, setAnnonce] = useState<string | null>(null);

  const jd = duJour.data;
  const jour = jd?.aujourdhui ?? aujourdhui();
  const question = auPouce && !!jd && poseLaQuestion(jd.journee);
  const ouvrirLaFiche = (t: { bulleId: string; parentId: string | null }) =>
    t.parentId
      ? naviguer(`/feuille/${t.parentId}`, { state: { ouvrirLaFiche: t.bulleId } satisfies ArriveeSurLaFeuille })
      : naviguer(`/feuille/${t.bulleId}`);
  // Plus de journée (sa dernière tâche sortie) : un écran ouvert dessus se referme.
  useEffect(() => {
    if (question) setEcran('en-cours');
  }, [question]);
  const revenir = (message?: string) => {
    setEcran('en-cours');
    if (message) setAnnonce(message);
  };

  /*
   * Au téléphone, le haut du carnet (chantier telephone-habit, 02), « Ma journée » à la main
   * comme la maquette (chantier telephone-ecrans, 01) : le ⚙ règle la journée,
   * « Changer ma journée » et « Recommencer » quand elle est prévue (les mêmes écrans qu'avant).
   */
  const prevue = !!jd?.journee && !question;
  const ouvrirDepuisLaFeuille = (fermer: () => void, vers: 'changer' | 'recommencer') => {
    fermer();
    setAnnonce(null);
    setEcran(vers);
  };

  return (
    <div className={`journee-ecran${couche ? ' journee-ecran--couche' : ''}`}>
      {auPouce ? (
        <HautDuPouce
          titre={MA_JOURNEE}
          couche={couche}
          dessus={<span className="haut-pouce-jour">{enFrancais(jour, true)}</span>}
          apres={couche ? <span className="haut-pouce-apres">{enFrancais(jour, true)}</span> : undefined}
          reglages={{
            titre: REGLER_MA_JOURNEE,
            contenu: (fermer) =>
              prevue && (
                <>
                  <button type="button" className="pouce-ligne-reg" onClick={() => ouvrirDepuisLaFeuille(fermer, 'changer')}>
                    {CHANGER_MA_JOURNEE}
                    <span aria-hidden="true">›</span>
                  </button>
                  <button type="button" className="pouce-ligne-reg" onClick={() => ouvrirDepuisLaFeuille(fermer, 'recommencer')}>
                    {RECOMMENCER}
                    <span aria-hidden="true">›</span>
                  </button>
                </>
              ),
          }}
        />
      ) : (
        <header className="journee-ecran-tete">
          <h1>{TA_JOURNEE}</h1>
          <p className="journee-ecran-date">{majuscule(enFrancais(jour, true))}</p>
        </header>
      )}

      {jd?.lendemain && <BandeauDuLendemain bandeau={jd.lendemain} aujourdhui={jd.aujourdhui} onAnnonce={setAnnonce} />}
      {jd?.veille && <BandeauDeLaVeille bandeau={jd.veille} aujourdhui={jd.aujourdhui} onAnnonce={setAnnonce} />}

      {jd && question && <PlanifierUnJour key={jd.aujourdhui} jour={jd.aujourdhui} onFermer={(message) => message && setAnnonce(message)} />}
      {jd && !question && auPouce && jd.journee && (
        <>
          <JourneeAuPouce
            donnees={{ ...jd, journee: jd.journee }}
            luA={duJour.dataUpdatedAt}
            onOuvrirLaFiche={ouvrirLaFiche}
            onChanger={() => {
              setAnnonce(null);
              setEcran('changer');
            }}
            onAnnonce={setAnnonce}
            couche={couche}
          />
          {ecran === 'changer' && (
            <ChangerMaJournee jour={jd.aujourdhui} retour={MA_JOURNEE} onRetour={() => revenir()} onRecommencer={() => setEcran('recommencer')} onFermer={revenir} />
          )}
          {ecran === 'recommencer' && (
            <PlanifierUnJour
              jour={jd.aujourdhui}
              plein
              retour={MA_JOURNEE}
              phrase={ON_RECOMMENCE}
              faites={jd.journee.taches.filter((t) => t.etat === 'fait')}
              onFermer={revenir}
            />
          )}
        </>
      )}
      {jd && !question && !auPouce && (
        <JourneeDuJour
          donnees={jd}
          onPlanifier={() => setPlanifier(true)}
          onOuvrirLaFiche={ouvrirLaFiche}
          onAnnonce={setAnnonce}
        />
      )}
      {annonce && (
        <p className="journee-annonce" role="status">
          {annonce}
        </p>
      )}
      {planifier && jd && !question && !auPouce && (
        <PreparerUnJour
          jour={jd.aujourdhui}
          onFermer={(message) => {
            setPlanifier(false);
            if (message) setAnnonce(message);
          }}
        />
      )}
    </div>
  );
}
